import React from "react";
import { FiAlertCircle } from "react-icons/fi";
import styles from "./styles.module.css";

export const brandAssets = {
  logo: `${process.env.PUBLIC_URL}/assets/images/bootsplash_logo@4x.png`,
  marty: `${process.env.PUBLIC_URL}/assets/images/martyCelebrating.png`,
};

export function BrandLogo({ className = "" }: { className?: string }) {
  return (
    <div className={`${styles.logo} ${className}`}>
      <img src={brandAssets.logo} alt="Robotical" />
    </div>
  );
}

export function MartyArtwork({ small = false }: { small?: boolean }) {
  return (
    <div className={`${styles.marty} ${small ? styles.smallMarty : ""}`}>
      <img src={brandAssets.marty} alt="" />
    </div>
  );
}

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
};

export function Button({
  variant = "secondary",
  className = "",
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={`${styles.button} ${styles[variant]} ${className}`}
      {...props}
    />
  );
}

export function Badge({
  children,
  status = "none",
}: {
  children: React.ReactNode;
  status?: "open" | "closed" | "none";
}) {
  return (
    <span className={`${styles.badge} ${styles[status]}`}>
      <span className={styles.statusDot} aria-hidden="true" />
      {children}
    </span>
  );
}

export function PageHeading({
  title,
  count,
  action,
}: {
  title: string;
  count?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      className={`${styles.pageHeading} ${action ? "" : styles.plainHeading}`}
    >
      <div className={styles.titleGroup}>
        <h1>{title}</h1>
        {count !== undefined && (
          <span className={styles.pageCount}>{count}</span>
        )}
      </div>
      {action}
    </div>
  );
}

export function FilterTabs<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string; count?: number }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className={styles.filters} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          className={`${styles.filter} ${value === option.value ? styles.selectedFilter : ""}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.count !== undefined && <span>{option.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className={styles.loading} role="status">
      <span className={styles.spinner} aria-hidden="true" />
      {label}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className={styles.error} role="alert">
      <FiAlertCircle aria-hidden="true" />
      <span>{message}</span>
      {onRetry && <Button onClick={onRetry}>Try again</Button>}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  children,
  artwork,
}: {
  title: string;
  description?: string;
  children?: React.ReactNode;
  artwork?: React.ReactNode;
}) {
  return (
    <div className={styles.empty}>
      {artwork}
      <h2>{title}</h2>
      {description && <p>{description}</p>}
      {children}
    </div>
  );
}

export function formatDate(value?: string | Date, short = false) {
  if (!value || Number.isNaN(new Date(value).getTime())) return "—";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: short ? "short" : "long",
    year: "numeric",
  });
}
